import { useQuery } from 'react-query'
import { useSearchParams } from 'react-router-dom'

import { useChosenCompany } from '../../../../../context/UserProvider'
import {
  ALL_CONSOLIDATE_CODES,
  type FleetFilter,
  toFleetFilter,
  useFleetCars,
  useFleetSummary,
} from '../../../../../hooks/useFleet'
import { Loading } from '../_shared/Loading'
import { LogoLoader } from '../_shared/LogoLoader'
import Layout from '../_shared/Layout'
import { CarParkOverviewList } from './components/CarParkOverview/CarParkOverviewList'
import { Stack } from '../_shared/Stack'
import type { SimpleVehicleProps } from '../types/types'
import type React from 'react'
import { useMemo, useRef, useState } from 'react'
import { CarParkSearchAndFilter } from './components/CarParkOverview/CarParkSearchAndFilter'
import { CarParkLoadProgress } from './components/CarParkOverview/CarParkLoadProgress'
import CarParkTable from './components/CarParkTable/CarParkTable'
import type { ColumnDef, VisibilityState } from '@tanstack/react-table'
import { fetchDeviations } from '../../../../../api/api'
import { mergeCarsAndDeviations } from '../helpers/mergeCarsAndDeviations'
import { filterVehicles, type VehicleFilters } from '../helpers/filterVehicles'
import { searchVehicles } from '../helpers/searchVehicles'
import { fleetFilterCount } from '../helpers/fleetFilterCount'
import { hasDeviationsOrWarnings } from '../helpers/hasDeviationsOrWarnings'
import { ExcelExportButton } from '../../../../reports/v3/components/ExcelExporter'
import { findColumns } from '../helpers/findColumns'
import { formatDate } from '../helpers/formatDate'
import { mapCarStatus } from '../../../../../utils/helpers/helpers.cars'

const EMPTY_FILTERS: VehicleFilters = {
  status: [],
  contactPerson: [],
  invoiceGroup: [],
  vehicleType: [],
  vehicleMake: [],
  fuelType: [],
  consolidateInvoiceCode: [],
}

export function CarPark({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  openEdit,
  openInfo,
  navigateToCar,
}: {
  columns: ColumnDef<SimpleVehicleProps, any>[]
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: (v: VisibilityState | ((old: VisibilityState) => VisibilityState)) => void
  openEdit?: (car: SimpleVehicleProps) => void
  openInfo?: (car: SimpleVehicleProps) => void
  navigateToCar?: (car: SimpleVehicleProps) => void
}) {
  const company = useChosenCompany()
  const [searchParams, setSearchParams] = useSearchParams()
  const [resetExpanded, setResetExpanded] = useState(false)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<VehicleFilters>(EMPTY_FILTERS)

  const filter = toFleetFilter(searchParams.get('filter'))
  const showDeviations = searchParams.get('deviations') === 'true'

  const { summary, isLoading: summaryLoading } = useFleetSummary(ALL_CONSOLIDATE_CODES)
  const filterCount = fleetFilterCount(summary, filter)

  const { cars, loadedCount, resetFilter, isLoading, isLoadingMore, isRefreshing } = useFleetCars(filter, filterCount)

  const {
    data: deviations,
    isLoading: deviationsLoading,
    isFetching: deviationsFetching,
    refetch: refetchDeviations,
  } = useQuery(['carDeviations', company?.id], async () => await fetchDeviations(company.id, 'car'), {
    enabled: !!company?.id,
  })

  const hasLoadedCarPark = useRef(false)
  if (cars) hasLoadedCarPark.current = true

  const carParkLoading = isLoading && !hasLoadedCarPark.current
  const deviationsLoadingState = deviationsLoading || deviationsFetching

  const mergedCars = useMemo(
    () => mergeCarsAndDeviations({ cars: cars ?? [], deviations: deviations as any }),
    [cars, deviations],
  )

  const baseCars = useMemo(
    () => (showDeviations ? mergedCars.filter(hasDeviationsOrWarnings) : mergedCars),
    [mergedCars, showDeviations],
  )

  const carList = useMemo(() => searchVehicles(filterVehicles(baseCars, filters), search), [baseCars, filters, search])

  function collapseExpandedRows() {
    setResetExpanded((prev) => !prev)
  }

  function changeSearch(next: string) {
    setSearch(next)
    collapseExpandedRows()
  }

  function changeFilters(next: React.SetStateAction<VehicleFilters>) {
    setFilters(next)
    collapseExpandedRows()
  }

  function changeFilter(next: FleetFilter) {
    resetFilter(next)
    const params = new URLSearchParams(searchParams)
    params.set('filter', next)
    setSearchParams(params, { replace: true })
    setFilters(EMPTY_FILTERS)
    setSearch('')
    collapseExpandedRows()
  }

  function toggleDeviations() {
    const params = new URLSearchParams(searchParams)
    if (showDeviations) {
      params.delete('deviations')
    } else {
      params.set('deviations', 'true')
      refetchDeviations()
    }
    setSearchParams(params, { replace: true })
    collapseExpandedRows()
  }

  if (carParkLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <Loading>
          <p>Henter bilpark...</p>
        </Loading>
      </div>
    )
  }

  const mappedExcelData = carList.map((car) => {
    const { actualDeliveryDate, plannedReturnDate, replacementCar, status, nextInspectionDate, ...rest } = car
    return {
      ...rest,
      status: mapCarStatus(status),
      actualDeliveryDate: formatDate(actualDeliveryDate),
      plannedReturnDate: formatDate(plannedReturnDate),
      nextInspectionDate: formatDate(nextInspectionDate),
      replacementCar: replacementCar?.registrationNumber || replacementCar?.accountNo || replacementCar?.budgetNo || '',
    }
  })

  return (
    <Layout className="pb-8">
      <Stack className="overflow-y-auto">
        <CarParkOverviewList
          cars={carList as SimpleVehicleProps[]}
          summary={summary}
          summaryLoading={summaryLoading}
          filter={filter}
          onFilterChange={changeFilter}
          showDeviations={showDeviations}
          onToggleDeviations={toggleDeviations}
          deviationsLoading={deviationsLoadingState}
        />
        <Stack spacing="sm">
          <div>
            <p className="text-2xl">Bilparken til {company.name}</p>
            {isLoadingMore && <CarParkLoadProgress loadedCount={loadedCount} totalCount={filterCount} />}
          </div>
          <CarParkSearchAndFilter
            search={search}
            setSearch={changeSearch}
            filters={filters}
            setFilters={changeFilters}
            cars={baseCars as SimpleVehicleProps[]}
          >
            <ExcelExportButton
              data={mappedExcelData}
              config={{
                columns: findColumns(),
                filename: `Bilpark-${company.name.replace(/\s/g, '-')}.xlsx`,
                sheetName: 'Car Cost Report',
              }}
            />
          </CarParkSearchAndFilter>
        </Stack>
        <Stack className="relative max-h-[800px] h-[60vh]">
          <CarParkTable
            columns={columns}
            resetExpanded={resetExpanded}
            cars={carList}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
            tableMeta={{ openEdit, navigateToCar }}
            openInfo={openInfo}
          />
          {isRefreshing && (
            <div className="absolute inset-0 z-[3] flex items-center justify-center overflow-hidden rounded-lg bg-white/70">
              <LogoLoader />
            </div>
          )}
        </Stack>
      </Stack>
    </Layout>
  )
}
